import React, { useState, useEffect, useMemo } from "react";
import "./../elementPages.less";
import dayjs from "dayjs";
import { Button, Dropdown, Checkbox, Tooltip } from "antd";
import { replaceFormulaStr } from "@/config/func";
import { rebuildFormula } from "@/config/func";
import { descMap } from "@/constants/desc";
import { useKeyComponents } from "../../ResultPageFrame";
import { formatBySig, resolveSig, formatErrorPercent, isNumValue } from "@/utils/numberFormat";

interface StreamsComponentTableProps {
  source: {
    inputStreams: any[];
    outStreams: any[];
  };
  materials: any[];
  selectTreeButton?: string;
  selectTreeButtonType?: string;
  input?: any;
  compareItems?: any[];
  streamDescMap?: Record<string, string>;
  streamNodeMap?: Record<string, { fromName: string; toName: string }>;
  elementDescMap?: Record<string, string>;
}

const dataModes = [
  { value: "molarFraction", label: "摩尔分率" }, 
  { value: "massFraction", label: "质量分率" }, 
  { value: "massFlow", label: "质量流量 （kg/h）" },
  { value: "molarFlow", label: "摩尔流量 （kmol/h）" },
  { value: "massConcentration", label: "质量浓度 (mg/Nm³)" },
];

const StreamsComponentTable = ({ source, materials, selectTreeButton, selectTreeButtonType, input, compareItems = [], streamDescMap = {}, streamNodeMap = {}, elementDescMap = {} }: StreamsComponentTableProps) => {
  const [dataMode, setDataMode] = useState("molarFraction");
  const [showAllData, setShowAllData] = useState(true);
  const { keyComponents, setKeyComponents, keyComponentsEverSet } = useKeyComponents();

  // 构建 material 标签（与塔组件 keyComponents 格式一致）
  const materialLabels = useMemo(() => materials.map((mat: any) => rebuildFormula(mat?.Molecular_formula || mat?.Name_ZH || "")), [materials]);

  // 默认关键组分：有甲醇或水则仅勾选甲醇/水，否则全选（仅在首次初始化）
  useEffect(() => {
    if (materials?.length > 0 && keyComponents.length === 0 && !keyComponentsEverSet.current) {
      //甲醇水
      // const waterMethanolLabels = materials
      //   .filter((mat: any) => {
      //     const name = mat?.Molecular_formula || mat?.Name_ZH || "";
      //     return name.includes("CH3OH") || name.includes("CH₃OH") || name.includes("H2O") || name.includes("H₂O");
      //   })
      //   .map((mat: any) => rebuildFormula(mat?.Molecular_formula || mat?.Name_ZH || ""));
      // const initial = waterMethanolLabels.length > 0 ? waterMethanolLabels : materialLabels;
      // setKeyComponents(initial);
      setKeyComponents(materialLabels);
      keyComponentsEverSet.current = true;
    }
  }, [materials, keyComponents.length, setKeyComponents, materialLabels]);

  // 获取输出流股的 rule 映射（用于 Flash2 闪蒸罐标识顶部/底部）
  const getStreamRuleMap = () => {
    const ruleMap: Record<string, string> = {};
    if (selectTreeButtonType === "Flash2" && input?.inputs?.elements) {
      const element = input.inputs.elements.find((e: any) => e.name === selectTreeButton);
      if (element?.outputStreams) {
        element.outputStreams.forEach((s: any) => {
          ruleMap[s.name] = s.rule;
        });
      }
    }
    return ruleMap;
  };
  const streamRuleMap = getStreamRuleMap();

  // 数值展示：按有效数字格式化（非 0 值不会显示成 0，极小/极大值用科学计数法），sig 由字段内自适应精度决定
  const safeToFixed = (value: any, sig: number = 6): any => {
    if (value === undefined || value === null || isNaN(value)) return "-";
    return formatBySig(value, sig);
  };

  // 计算误差百分比
  const calculateErrorPercent = (compareValue: number, calcValue: number): string => {
    // 判断设计值/实际值/任务值和计算值是否都为 0
    if (compareValue === 0 && calcValue === 0) {
      return "0%";
    }
    // 如果计算值不为 0 但设计值/实际值/任务值为 0
    if (compareValue === 0 && calcValue !== 0) {
      return "100%";
    }
    // 正常计算误差百分比：(计算值 - 设计值/实际值/任务值) ÷ 设计值/实际值/任务值
    const errorPercent = ((calcValue - compareValue) / compareValue) * 100;
    // 非 0 但极小的误差显示 <0.01%，避免出现"0.00% 却被标红"的矛盾
    return formatErrorPercent(errorPercent);
  };

  // 判断当前值与对比值是否有差异（用于整行标红，与输入参数对比高亮方式一致）
  const valuesDiffer = (current: any, compare: any): boolean => {
    // 缺失值：- / null / undefined
    const curMissing = current === "-" || current === null || current === undefined;
    const cmpMissing = compare === "-" || compare === null || compare === undefined;
    // 任一侧缺失（页面显示为 "-"）都不算差异，不标红
    if (curMissing || cmpMissing) return false;
    const curStr = String(current);
    const cmpStr = String(compare);
    const curNum = parseFloat(curStr);
    const cmpNum = parseFloat(cmpStr);
    const curIsNum = !isNaN(curNum);
    const cmpIsNum = !isNaN(cmpNum);
    if (curIsNum && cmpIsNum) {
      // 数值比较：相对容差比较，忽略浮点噪声（相对误差 < 0.0000001% 视为相等，与显示一致）
      if (curNum === cmpNum) return false;
      const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
      return Math.abs(curNum - cmpNum) > tolerance;
    }
    // 非数值：字符串直接比较
    return curStr !== cmpStr;
  };

  // 🔴 适配新结构：item.values.mix_input.streams
  const getCompareStreams = (streamName: string) => {
    return compareItems.map((item) => ({
      ...item,
      // 优先用各页面统一上报的输出换算数据（fullOutput），否则回退 mix_input
      stream: item?.values?.fullOutput?.streams?.find((s: any) => s.name === streamName)
        || item?.values?.mix_input?.streams?.find((s: any) => s.name === streamName),
    }));
  };

  // ========== 行列过滤逻辑 ==========

  // 获取某个 stream 某个材料索引的计算值
  const getCalcVal = (stream: any, matIndex: number): number => {
    const massFrac = stream.mass_frac?.[matIndex] || 0;
    const molarFrac = stream.molar_frac?.[matIndex] || 0;
    const fKgPerH = stream.f_kg_per_h || 0;
    const fMolPerH = stream.f_mol_per_h || 0;
    const fNm3PerH = stream.f_nm3_per_h || stream.f_m3_per_h || 1;
    switch (dataMode) {
      case "massFlow": return fKgPerH * massFrac;
      case "massFraction": return massFrac;
      case "molarFlow": return (fMolPerH * molarFrac) / 1000;
      case "massConcentration": return (fKgPerH * 1e6 * massFrac) / fNm3PerH;
      case "molarFraction": default: return molarFrac;
    }
  };

  // 获取某个 stream 某个材料索引的某个对比值
  const getCompareVal = (stream: any, matIndex: number, compareItem: any): number | null => {
    const cs = compareItem?.values?.mix_input?.streams?.find((s: any) => s.name === stream.name);
    if (!cs?.materials) return null;
    const compareMat = cs.materials[matIndex];
    if (!compareMat) return null;
    const compareMassFlow = cs.params?.f || 0;
    // 保留 null（缺失组分），显示为 "-"
    const compareMolarFrac = compareMat.molar_frac ?? null;
    const matMW = materials[matIndex]?.MW || 1;
    const compareAvgMW = cs.materials.reduce((sum: number, m: any, i: number) => {
      return sum + (m.molar_frac || 0) * (materials[i]?.MW || 1);
    }, 0);
    const compareTotalMolarFlow = compareAvgMW ? ((compareMassFlow / compareAvgMW) * 1000) / 1000 : 0;

    switch (dataMode) {
      case "molarFlow": return compareMolarFrac == null ? null : compareTotalMolarFlow * compareMolarFrac;
      case "molarFraction": return compareMolarFrac;
      case "massFraction": {
        if (!cs.mass_frac || cs.mass_frac[matIndex] == null) return null;
        return cs.mass_frac[matIndex];
      }
      case "massFlow": {
        if (!cs.mass_frac || cs.mass_frac[matIndex] == null) return null;
        return (cs.params?.f || 0) * cs.mass_frac[matIndex];
      }
      case "massConcentration": {
        if (!cs.mass_frac || cs.mass_frac[matIndex] == null) return null;
        const volFlow = cs.f_nm3_per_h || cs.f_m3_per_h || 1;
        return ((cs.params?.f || 0) * cs.mass_frac[matIndex] * 1e6) / volFlow;
      }
      default: return null;
    }
  };

  const isZeroOrInvalid = (val: any): boolean => {
    if (val === null || val === undefined || val === "-") return true;
    return Number(val) === 0;
  };

  // 对给定的 streams 数组进行行列过滤
  const filterStreams = (streams: any[]) => {
    const matIndices = materials.map((_, i) => i);
    if (!streams.length) return { visibleStreams: streams, visibleMatIndices: matIndices };
    if (showAllData) return { visibleStreams: streams, visibleMatIndices: matIndices };

    // 过滤行
    const visibleStreams = streams.filter((stream) => {
      return !matIndices.every((idx) => {
        const cv = getCalcVal(stream, idx);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareItems.length; i++) {
          const cv2 = getCompareVal(stream, idx, compareItems[i]);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    // 过滤列
    const visibleMatIndices = matIndices.filter((idx) => {
      return !streams.every((stream) => {
        const cv = getCalcVal(stream, idx);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareItems.length; i++) {
          const cv2 = getCompareVal(stream, idx, compareItems[i]);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    return { visibleStreams, visibleMatIndices };
  };

  const renderHeaders = (visibleMatIndices: number[]) => {
    const compareCount = compareItems.length;
    const colSpanPerMat = 1 + compareCount;

    const mainHeaders = [
      <th key="stream" rowSpan={2} className="table-header-cell stream-col">流股</th>,
      <th key="desc" rowSpan={2} className="table-header-cell">流股描述</th>,
      <th key="src" rowSpan={2} className="table-header-cell">源</th>,
      <th key="tgt" rowSpan={2} className="table-header-cell">目标</th>,
    ];

    visibleMatIndices.forEach((index) => {
      const material = materials[index];
      const matName = material.Molecular_formula || material.Name_ZH;
      mainHeaders.push(
        <th key={`mat-${index}`} colSpan={colSpanPerMat} className="table-header-cell">
          {replaceFormulaStr(matName)}
        </th>
      );
    });

    const subHeaders = [];
    if (compareCount > 0) {
      visibleMatIndices.forEach((index) => {
        subHeaders.push(<th key={`calc-${index}`} className="table-header-cell sub-header">当前计算值</th>);
        compareItems.forEach((item, idx) => {
          subHeaders.push(
            <th key={`compare-${idx}-${index}`} className="table-header-cell sub-header">
              {item.type === "task" 
                ? item.values.name 
                : item.type === "otherCalc"
                ? `${item.values.name} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}
            </th>
          );
        });
      });
    }

    return (
      <>
        <tr className="table-header">{mainHeaders}</tr>
        {compareCount > 0 && <tr className="table-header">{subHeaders}</tr>}
      </>
    );
  };

  const renderRow = (stream: any, visibleMatIndices: number[], isInput: boolean) => {
    const compareStreams = getCompareStreams(stream.name);
    
    // 流股名后标注输入/输出（Flash2 输出流股按 rule 标注顶部/底部）
    let streamDisplayName = stream.name;
    if (selectTreeButtonType === "Flash2" && streamRuleMap[stream.name]) {
      const rule = streamRuleMap[stream.name];
      if (rule === "D") {
        streamDisplayName = `${stream.name}（输出顶部）`;
      } else if (rule === "B") {
        streamDisplayName = `${stream.name}（输出底部）`;
      }
    } else {
      streamDisplayName = `${stream.name}（${isInput ? "输入" : "输出"}）`;
    }
    
    const rowCells = [
      <td key="name" className="table-cell stream-col">{streamDisplayName || "N/A"}</td>,
      <td key="desc" className="table-cell">{streamDescMap[stream.name] || "—"}</td>,
      <td key="src" className="table-cell">
        <Tooltip title={elementDescMap[streamNodeMap[stream.name]?.fromName] || "暂无描述"}>
          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.name]?.fromName || "—"}</span>
        </Tooltip>
      </td>,
      <td key="tgt" className="table-cell">
        <Tooltip title={elementDescMap[streamNodeMap[stream.name]?.toName] || "暂无描述"}>
          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.name]?.toName || "—"}</span>
        </Tooltip>
      </td>,
    ];

    visibleMatIndices.forEach((index: number) => {
      const material = materials[index];
      // 计算值
      let calcValue = 0;
      const massFrac = stream.mass_frac?.[index] || 0;
      const molarFrac = stream.molar_frac?.[index] || 0;
      const fKgPerH = stream.f_kg_per_h || 0;
      const fMolPerH = stream.f_mol_per_h || 0;
      const fNm3PerH = stream.f_nm3_per_h || stream.f_m3_per_h || 1;

      switch (dataMode) {
        case "massFlow":
          calcValue = fKgPerH * massFrac;
          break;
        case "massFraction":
          calcValue = massFrac;
          break;
        case "molarFlow":
          // 单位换算：从 mol/h 转换为 kmol/h（除以 1000）
          calcValue = (fMolPerH * molarFrac) / 1000;
          break;
        case "massConcentration":
          calcValue = (fKgPerH * 1e6 * massFrac) / fNm3PerH;
          break;
        case "molarFraction":
          calcValue = molarFrac;
          break;
      }
      // 先计算该组分下所有对比值，并判断是否有差异
      const compareCells = compareStreams.map((cs, idx) => {
        if (!cs.stream?.materials || !cs.stream.materials[index]) {
          // 对比值缺失（显示为 "-"）：不算差异，不标红
          return { value: "-", errorPercent: "-", diff: valuesDiffer(calcValue, null) };
        }
        const compareMat = cs.stream.materials[index];

        const compareMassFlow = cs.stream.params?.f || 0;
        // 保留 null（缺失组分），显示为 "-"
        const compareMolarFrac = compareMat.molar_frac ?? null;
        const matMW = material.MW || 1;
        const compareAvgMW = cs.stream.materials.reduce((sum: number, m: any, i: number) => {
          return sum + (m.molar_frac || 0) * (materials[i]?.MW || 1);
        }, 0);
        // 单位换算：从 mol/h 转换为 kmol/h（除以 1000）
        const compareTotalMolarFlow = compareAvgMW ? ((compareMassFlow / compareAvgMW) * 1000) / 1000 : 0;

        let designValue = 0;
        let showDash = false;
        switch (dataMode) {
          case "massFlow":
            if (!cs.stream.mass_frac || cs.stream.mass_frac[index] == null) {
              showDash = true;
            } else {
              designValue = (cs.stream.params?.f || 0) * cs.stream.mass_frac[index];
            }
            break;
          case "massFraction":
            if (!cs.stream.mass_frac || cs.stream.mass_frac[index] == null) {
              showDash = true;
            } else {
              designValue = cs.stream.mass_frac[index];
            }
            break;
          case "molarFlow":
            if (compareMolarFrac == null) {
              showDash = true;
            } else {
              designValue = compareTotalMolarFlow * compareMolarFrac;
            }
            break;
          case "massConcentration":
            if (!cs.stream.mass_frac || cs.stream.mass_frac[index] == null) {
              showDash = true;
            } else {
              const volFlow = cs.stream.f_nm3_per_h || cs.stream.f_m3_per_h || 1;
              designValue = ((cs.stream.params?.f || 0) * cs.stream.mass_frac[index] * 1e6) / volFlow;
            }
            break;
          case "molarFraction":
            if (compareMolarFrac == null) {
              showDash = true;
            } else {
              designValue = compareMolarFrac;
            }
            break;
        }
        // 计算误差百分比
        const errorPercent = !showDash && designValue !== 0
          ? calculateErrorPercent(designValue, calcValue)
          : (showDash ? "-" : "0.00%");
        // 对比值缺失（显示 -）不算差异，不标红；两边都有值时才比较
        return {
          value: showDash ? "-" : designValue,
          errorPercent,
          diff: !showDash && valuesDiffer(calcValue, designValue),
        };
      });
      // 该组分内自适应精度：常态 6 位有效数字，有差异时提升精度，保证差异在显示上也能看出来
      const sig = resolveSig([calcValue, ...compareCells.map((c) => c.value)], valuesDiffer);
      // 该组分下任一对比值与计算值有差异，则该组分的计算值+所有对比值单元格标红
      const matHasDiff = compareCells.some((c) => c.diff);
      rowCells.push(
        <td key={`calc-${index}`} className={`table-cell ${matHasDiff ? "compare-cell-diff" : ""}`} title={String(calcValue)}>
          {safeToFixed(calcValue, sig)}
        </td>
      );
      compareCells.forEach((c, idx) => {
        rowCells.push(
          <td key={`compare-${idx}-${index}`} className={`table-cell ${matHasDiff ? "compare-cell-diff" : ""}`} title={String(c.value)}>
            {c.value === "-" ? "-" : `${safeToFixed(c.value, sig)}(${c.errorPercent})`}
          </td>
        );
      });
    });

    return <tr key={`${stream.name}-${isInput ? "in" : "out"}`} className="table-row">{rowCells}</tr>;
  };

  return (
    <div className="steams-component-table-container">
      

      {/* 计算过滤后的数据 */}
      {(() => {
        const inputFiltered = filterStreams(source.inputStreams);
        const outputFiltered = filterStreams(source.outStreams);
        const allVisibleMatIndices = Array.from(new Set([...inputFiltered.visibleMatIndices, ...outputFiltered.visibleMatIndices])).sort((a, b) => a - b);
        // 将关键组分标签转为索引，用于表格列过滤
        const displayMatIndices = materialLabels
          .map((label, i) => ({ label, i }))
          .filter(({ label }) => keyComponents.includes(label))
          .map(({ i }) => i);
        const totalCols = 4 + displayMatIndices.length * (1 + compareItems.length);
        console.log("inputFiltered.visibleStreams", inputFiltered.visibleStreams);
        return (
          <>
            <div className="table-container">
              <h3 className="stream-title">流股组分数据：</h3>
              <div className="mode-selector-container" style={{ position: "relative" }}>
                <span className="mode-selector-label">显示数据切换：</span>
                <div className="mode-buttons">
                  {dataModes.map((mode) => (
                    <button
                      key={mode.value}
                      className={`mode-button ${dataMode === mode.value ? "active" : ""}`}
                      onClick={() => setDataMode(mode.value)}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                <Dropdown
                  trigger={["click"]}
                  overlay={
                    <div style={{ padding: 12, background: "#fff", minWidth: 200 }}>
                      <div style={{ marginBottom: 8, fontWeight: 600 }}>选择关键组分：</div>
                      <Checkbox.Group
                        value={keyComponents}
                        onChange={(checkedValues) => setKeyComponents(checkedValues as string[])}
                        style={{ display: "flex", flexDirection: "column", gap: 8 }}
                      >
                        {materials.map((mat: any, i: number) => (
                          <Checkbox key={i} value={materialLabels[i]}>
                            {replaceFormulaStr(mat?.Molecular_formula || mat?.Name_ZH || `组分${i + 1}`)}
                          </Checkbox>
                        ))}
                      </Checkbox.Group>
                      <div style={{ marginTop: 8, borderTop: "1px solid #f0f0f0", paddingTop: 8, display: "flex", gap: 12 }}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setKeyComponents(materialLabels)}
                        >
                          全选
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setKeyComponents([])}
                        >
                          取消全选
                        </Button>
                      </div>
                    </div>
                  }
                >
                  <Button style={{ fontSize: 14, position: "absolute", right: "0", top: 0, display: "inline-flex", gap: 0 }}>
                    <span style={{ fontSize: 18 }}>⚙️</span>设置关键组分
                  </Button>
                </Dropdown>
              </div>
              <div className="table-wrapper">
                <table key={`component-${compareItems.length}`} className="component-data-table">
                  <thead>{renderHeaders(displayMatIndices)}</thead>
                  <tbody>
                    {(() => {
                      // 输入 + 输出合并为一个表格，流股名后标注（输入）/（输出）
                      const mergedStreams = [
                        ...inputFiltered.visibleStreams.map((s: any) => ({ stream: s, isInput: true })),
                        ...outputFiltered.visibleStreams.map((s: any) => ({ stream: s, isInput: false })),
                      ];
                      return mergedStreams.length > 0 ? (
                        mergedStreams.map(({ stream, isInput }) => renderRow(stream, displayMatIndices, isInput))
                      ) : (
                        <tr className="empty-row">
                          <td colSpan={totalCols} className="empty-cell">暂无流股组分数据</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default StreamsComponentTable;